import { SentryError } from '@ifixit/sentry';
import {
   Avatar,
   Box,
   BoxProps,
   Link,
   LinkProps,
   Menu,
   MenuButton,
   MenuButtonProps,
   MenuItem,
   MenuProps,
   Text,
} from '@chakra-ui/react';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { FaIcon } from '@ifixit/icons';
import { createContext, useContext, useMemo, useEffect } from 'react';
import type { ReactNode } from 'react';
import { usePreloadImage } from '../hooks';
import { BlueDot } from '../misc';

type UserMenuContext = {
   user: User;
};

type User = {
   username: string;
   handle: string | null;
   thumbnail: string | null;
   partner: 'Back Market' | 'THD' | null;
};

const UserMenuContext = createContext<UserMenuContext | null>(null);

const useMenuContext = () => {
   const context = useContext(UserMenuContext);
   if (context == null) {
      throw new SentryError('useMenuContext must be used within a UserMenu');
   }
   return context;
};

export type UserMenuProps = MenuProps & {
   user: User;
};

export const UserMenu = ({ user, ...otherProps }: UserMenuProps) => {
   const value = useMemo((): UserMenuContext => ({ user }), [user]);

   return (
      <UserMenuContext.Provider value={value}>
         <Menu {...otherProps} />
      </UserMenuContext.Provider>
   );
};

interface UserMenuButtonProps extends MenuButtonProps {
   hasUnreadNotifications?: boolean;
}

export const UserMenuButton = ({ hasUnreadNotifications, ...props }: UserMenuButtonProps) => (
   <MenuButton
      position="relative"
      borderRadius="full"
      _focus={{
         boxShadow: 'outline',
         outline: 'none',
      }}
      {...props}
   >
      <UserAvatar />
      {hasUnreadNotifications && <BlueDot position="absolute" top="-2px" right="-2px" />}
   </MenuButton>
);

export const UserMenuHeading = (props: BoxProps) => {
   const { user } = useMenuContext();
   return (
      <Box px="4" py="2" {...props}>
         <Text color="gray.900" fontWeight="medium">
            {user?.username}
         </Text>
         {user?.handle && <Text color="gray.500">@{user?.handle}</Text>}
      </Box>
   );
};

interface UserMenuLinkProps {
   href: string;
   children: ReactNode;
}

export const UserMenuLink = ({ href, children }: UserMenuLinkProps) => (
   <MenuItem as="a" href={href} fontSize="sm" px="4" py="1.5">
      {children}
   </MenuItem>
);

interface MenuItemIconProps {
   icon: FontAwesomeIconProps['icon'];
}

export const MenuItemIcon = ({ icon }: MenuItemIconProps) => (
   <FaIcon icon={icon} w="4" color="gray.500" mr="1.5" />
);

export const NoUserLink = (props: LinkProps) => (
   <Link
      borderRadius="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      {...props}
      h={8}
      w={8}
      _hover={{ opacity: '0.7' }}
      transition="0.3s"
      aria-label="Login"
   >
      <FaIcon icon={faUser} h="5" color="white" />
   </Link>
);

export function BackMarketIcon() {
   return (
      <svg
         width="128"
         height="128"
         viewBox="0 0 128 128"
         fill="none"
         xmlns="http://www.w3.org/2000/svg"
      >
         <rect width="128" height="128" fill="#DDF869" />
         <path
            d="M52.9046 90H68.4538C68.8787 90 69.0911 89.4922 68.7906 89.1917L44.1585 64.5596C43.8528 64.2487 43.8528 63.7513 44.1585 63.4456L68.7906 38.8135C69.0911 38.5181 68.8787 38.0052 68.4538 38.0052H52.9046C52.4072 38.0052 51.9357 38.2021 51.5834 38.5544L26.6922 63.4456C26.3865 63.7565 26.3865 64.2539 26.6922 64.5596L51.5834 89.4508C51.9357 89.8031 52.4072 90 52.9046 90Z"
            fill="#061743"
         />
         <path
            d="M101.397 89.1917L76.7647 64.5596V64.5544C76.459 64.2435 76.459 63.7461 76.7647 63.4404L101.397 38.8083C101.697 38.513 101.485 38 101.06 38H85.5108C85.0134 38 84.5419 38.1969 84.1896 38.5492L59.2984 63.4404C58.9927 63.7513 58.9927 64.2487 59.2984 64.5544L84.1896 89.4508C84.5419 89.8031 85.0134 90 85.5108 90H101.06C101.485 90 101.697 89.4922 101.397 89.1917Z"
            fill="#061743"
         />
      </svg>
   );
}

function UserAvatar() {
   const { user } = useMenuContext();
   const image = usePreloadImage();

   const isBackMarketPro = user.partner === 'Back Market';

   useEffect(() => {
      if (user?.thumbnail) {
         image.preload(user.thumbnail);
      }
   }, [user?.thumbnail, image.preload]);

   return (
      <Avatar
         name={user?.thumbnail == null || image.isLoaded ? user?.username : undefined}
         src={isBackMarketPro ? undefined : (user?.thumbnail ?? undefined)}
         icon={
            isBackMarketPro ? <BackMarketIcon /> : <FaIcon icon={faUser} h="4" color="gray.300" />
         }
         bg={user?.thumbnail == null ? undefined : 'transparent'}
         size="sm"
      />
   );
}
